"use client";

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import type { Notification } from "@/types/topic";

interface NotificationsResponse {
  notifications: Notification[];
}

/**
 * Notification polling hook.
 *
 * Polls /api/notifications?unread=true every 15 seconds.
 * Returns the unread count and notification list.
 * Does not poll when the browser tab is hidden.
 */
export function useNotifications() {
  return useQuery<NotificationsResponse>({
    queryKey: ["notifications", "unread"],
    queryFn: async () => {
      const res = await fetch("/api/notifications?unread=true");
      if (!res.ok) throw new Error("Failed to fetch notifications");
      return res.json();
    },
    refetchInterval: 15_000, // Poll every 15 seconds
    refetchIntervalInBackground: false, // Don't poll when tab hidden
  });
}

/**
 * Mutation hook to mark a notification as read.
 * Invalidates the notification query on success.
 */
export function useMarkNotificationRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (notificationId: string) => {
      const res = await fetch("/api/notifications", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: notificationId }),
      });
      if (!res.ok) throw new Error("Failed to mark notification as read");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
    },
  });
}
